<script>
  import { onMount } from 'svelte';
  import { format } from 'svelte-i18n';
  import { Button } from '@sveltestrap/sveltestrap';

  import { Capability } from '@client/components/capability';
  import { MultiSelectField } from '@client/components/fields/general';
  import { getNotificationsContext } from '@client/components/notifications';
  import { ParamFields } from '@client/components/param-fields';
  import { SectionItem } from '@client/components/section';
  import { Step } from '@client/components/steps';
  import { UserFeatures } from '@client/components/user-features';
  import { UserNotifications } from '@client/components/user-notifications';
  import { UserSelect } from '@client/components/user-select';

  import { UsersService } from '@client/services/users';

  import { appStore } from '@client/stores/app';

  import { USER_ROLES } from '@client/enums';

  import { deepCopy } from '@common/utils/objects';

  /**
   * Controls whether the next button in the workflow step is disabled.
   * @type {boolean}
   * @default true
   */
  export let nextDisabled = true;

  /**
   * Callback function invoked when the workflow step executes.
   * @type {Function | null}
   * @default null
   */
  export let onExecute = null;

  /**
   * Callback function invoked when the workflow step is rolled back.
   * @type {Function | null}
   * @default null
   */
  export let onRollback = null;

  /**
   * Step configuration object containing step data and metadata.
   * @type {Object}
   * @default { data: {} }
   */
  export let step = { data: {} };

  const { addNotification } = getNotificationsContext();

  const notify = (text, type = 'success', autohide = 2500, position = 'top-right') => {
    addNotification({ text, type, autohide, position });
  };

  let currentStep = 0;
  let maxStepReached = 0;

  // User selection state
  let users = [];
  let selectedUser = null;

  // Edit form state
  let user = null;
  let originalUser = null;
  let parameters = [];

  // Settings state
  let usersRoles = Object.keys(USER_ROLES || {}).map((role) => ({
    label: $format(USER_ROLES[role].label.short),
    value: USER_ROLES[role].id,
    checked: false
  }));
  let selectedUserRoles = [];
  let usersettings = {};
  let inactive = false;

  const onSelectUser = async (event) => {
    const userData = event.detail;

    let usermeta = await UsersService.getUserById(userData.email);

    if (!userData || !usermeta) {
      selectedUser = null;
      user = null;
      originalUser = null;
      parameters = [];
      return;
    }

    const name = usermeta.email
      .split('@')[0]
      .split('.')
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');

    usermeta = {
      ...usermeta,
      label: name,
      value: usermeta.email
    };

    selectedUser = usermeta;
    originalUser = deepCopy(usermeta);
    user = deepCopy(usermeta);
    parameters = deepCopy(usermeta.params || []);

    // Initialize settings state from user data
    selectedUserRoles = usersRoles
      .map((config) => {
        if (usermeta.roles && usermeta.roles[config.value]) {
          return {
            label: $format(USER_ROLES[config.value].label.short),
            value: config.value,
            checked: true
          };
        }
        return false;
      })
      .filter(Boolean);

    usersettings = usermeta.usersettings || {};
    inactive = usermeta.removed || false;
  };

  const onRoleUpdate = (id, items, selected) => {
    selectedUserRoles = selected;
  };

  const onToggleUserState = async () => {
    inactive = !inactive;
    user.removed = inactive;
  };

  const handleNext = () => {
    if (selectedUser && user) {
      currentStep = 1;
      if (currentStep > maxStepReached) {
        maxStepReached = currentStep;
      }
    }
  };

  const handleBack = (stepIndex) => {
    if (stepIndex > 0) {
      currentStep = stepIndex - 1;
    }
  };

  const executeUpdate = async () => {
    $appStore.loading = true;

    try {
      // Build roles object from selected roles
      const roles = {};
      selectedUserRoles.forEach(({ value }) => (roles[value] = true));

      const response = await UsersService.updateUser({
        ...user,
        roles,
        params: [...parameters],
        usersettings,
        removed: inactive,
        updateType: 'params'
      });

      if (response?.status === 400) {
        notify($format('label.FAILURE.UPDATE_USER', { values: { message: response.message } }), 'error');
        throw new Error($format('label.FAILURE.UPDATE_USER'));
      }

      notify($format('label.SUCCESS.UPDATE', { values: { item: user.name || user.email } }));

      step.data = {
        id: user.email.split('@')[0],
        rollback: originalUser
      };

      return { success: true };
    } catch (error) {
      notify($format('label.FAILURE.UPDATE_USER'), 'error');
      throw error;
    } finally {
      $appStore.loading = false;
    }
  };

  const rollbackUpdate = async () => {
    $appStore.loading = true;

    try {
      if (!step.data?.rollback) {
        throw new Error($format('label.NO_ROLLBACK_DATA'));
      }

      const rollbackData = step.data.rollback;

      const response = await UsersService.updateUser({
        ...rollbackData,
        roles: rollbackData.roles || {},
        params: [...(rollbackData.params || [])],
        usersettings: rollbackData.usersettings || {},
        removed: rollbackData.removed || false,
        updateType: 'params'
      });

      if (response?.status === 400) {
        notify($format('label.FAILURE.UPDATE_USER'), 'error');
        throw new Error($format('label.FAILURE.ROLLBACK_USER_UPDATE'));
      }

      notify($format('label.SUCCESS.ROLLBACK_USER_UPDATE'));

      return { success: true };
    } catch (error) {
      notify($format('label.FAILURE.ROLLBACK_USER_UPDATE'), 'error');
      throw error;
    } finally {
      $appStore.loading = false;
    }
  };

  onMount(() => {
    if (onExecute) {
      onExecute(executeUpdate);
    }

    if (onRollback) {
      onRollback(rollbackUpdate);
    }
  });

  $: isStep0Disabled = !selectedUser || !user;

  $: hasParamsChanges =
    user && originalUser && JSON.stringify(parameters) !== JSON.stringify(originalUser.params || []);

  $: hasRolesChanges = (() => {
    if (!user || !originalUser) return false;
    const currentRoles = selectedUserRoles.map((r) => r.value).sort();
    const originalRoles = Object.keys(originalUser.roles || {})
      .filter((k) => originalUser.roles[k])
      .sort();
    return JSON.stringify(currentRoles) !== JSON.stringify(originalRoles);
  })();

  $: hasSettingsChanges =
    user && originalUser && JSON.stringify(usersettings) !== JSON.stringify(originalUser.usersettings || {});

  $: hasInactiveChanges = user && originalUser && inactive !== (originalUser.removed || false);

  $: hasChanges = hasParamsChanges || hasRolesChanges || hasSettingsChanges || hasInactiveChanges;

  $: isStep1Disabled = !hasChanges;

  $: nextDisabled = currentStep !== 1 || isStep1Disabled;

  $: selectedUserSummary = selectedUser?.label || $format('label.NOT_SELECTED');
</script>

<div class="user-update-form">
  <Step
    active={currentStep === 0}
    disabled={false}
    title={$format('label.SELECT_USER')}
    summary={selectedUserSummary}
    onNext={currentStep === 0 ? handleNext : undefined}
    onStep={() => (currentStep = 0)}
    nextDisabled={isStep0Disabled}
  >
    <SectionItem label={$format('label.SELECT_USER')} description={$format('label.CHOOSE_USER_TO_UPDATE')}>
      <UserSelect bind:users value={selectedUser} isClearable={false} showChevron on:change={onSelectUser} />
    </SectionItem>
  </Step>

  <Step
    active={currentStep === 1}
    disabled={maxStepReached < 1}
    title={$format('label.EDIT_USER')}
    summary={user?.name || user?.email || $format('label.NOT_CONFIGURED')}
    onBack={currentStep === 1 ? () => handleBack(1) : undefined}
    onStep={() => {
      if (currentStep >= 1) currentStep = 1;
    }}
    nextDisabled={isStep1Disabled}
  >
    {#if user}
      <div class="edit-form-wrapper">
        <SectionItem label="Fields" description="Manage user fields and their values">
          <ParamFields bind:params={parameters} compliance={user?.sigcompliance} framed />
        </SectionItem>

        <Capability action="manage" subject="domainadmin">
          <SectionItem label={$format('label.GENERAL')} description="Manage user roles">
            <MultiSelectField
              id="roles"
              label="Roles"
              placeholder={$format('label.PLACEHOLDER.SELECT_ROLES')}
              bind:options={usersRoles}
              bind:selected={selectedUserRoles}
              onUpdate={onRoleUpdate}
            />
          </SectionItem>
        </Capability>

        <SectionItem label={$format('label.FEATURES')} description="Configure user features">
          <UserFeatures bind:settings={usersettings} />
        </SectionItem>

        {#if usersettings.enablesales}
          <SectionItem label={$format('label.NOTIFICATIONS')} description="Configure user notifications">
            <UserNotifications bind:settings={usersettings} expandAll />
          </SectionItem>

          <SectionItem
            label={inactive ? $format('label.ENABLE_USER') : $format('label.DISABLE_USER')}
            description={inactive
              ? $format('label.DESCRIPTION.ENABLE_USER')
              : $format('label.DESCRIPTION.DISABLE_USER')}
          >
            {#if !inactive}
              <Button color="danger" on:click={onToggleUserState}>
                {$format('label.DISABLE_USER')}
              </Button>
            {:else}
              <Button on:click={onToggleUserState}>{$format('label.ENABLE_USER')}</Button>
            {/if}
          </SectionItem>
        {/if}
      </div>
    {/if}
  </Step>
</div>

<style lang="scss">
  .user-update-form {
    .edit-form-wrapper :global {
      .section {
        margin-bottom: var(--margin-large);

        h2 {
          font-size: var(--font-size-large);
          margin-bottom: var(--margin-base);
        }
      }
    }
  }
</style>
